import type {
  CustomerBillingAccessState,
  CustomerBillingPreference,
  CustomerBillingStatus,
  CustomerRegistrationMode,
  ICustomer,
} from '../services/registrations/customers/types';
import { getSubscriptionPlanSlug } from './subscriptionPlan';

export type { CustomerBillingAccessState };

export interface CustomerSubscriptionSnapshot {
  registrationMode: CustomerRegistrationMode | null;
  billingStatus: CustomerBillingStatus | null;
  accessState: CustomerBillingAccessState | null;
  accessBlockedAt: string | null;
  pastDueAt: string | null;
  billingPreference: CustomerBillingPreference | null;
  chargedPriceCents: number | null;
  commitmentEndsAt: string | null;
  commitmentDaysRemaining: number | null;
  trialDaysRemaining: number | null;
  planSlug: string | null;
  planName: string | null;
  isAiActivated: boolean;
}

const EMPTY_SNAPSHOT: CustomerSubscriptionSnapshot = {
  registrationMode: null,
  billingStatus: null,
  accessState: null,
  accessBlockedAt: null,
  pastDueAt: null,
  billingPreference: null,
  chargedPriceCents: null,
  commitmentEndsAt: null,
  commitmentDaysRemaining: null,
  trialDaysRemaining: null,
  planSlug: null,
  planName: null,
  isAiActivated: false,
};

export const COMMITMENT_RENEWAL_WARNING_DAYS = 30;
export const COMMITMENT_RENEWAL_URGENT_DAYS = 7;
export const COMMITMENT_RENEWAL_CRITICAL_DAYS = 1;

export type CommitmentEndingSeverity = 'info' | 'warning' | 'error';

export const getDaysUntilDate = (isoDate: string | null | undefined): number | null => {
  if (!isoDate) return null;

  const target = new Date(isoDate);
  if (Number.isNaN(target.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

export const getOwnedCustomer = (
  customers: ICustomer[] | undefined,
  userId?: string | null
): ICustomer | null => {
  if (!customers?.length) return null;
  if (!userId) return customers[0] ?? null;
  return customers.find((customer) => customer.mainUser?.id === userId) ?? customers[0] ?? null;
};

export const mapCustomerToSubscriptionSnapshot = (
  customer: ICustomer | null | undefined
): CustomerSubscriptionSnapshot => {
  if (!customer) return { ...EMPTY_SNAPSHOT };

  const plan = typeof customer.subscriptionPlan === 'object' ? customer.subscriptionPlan : null;
  const commitmentDaysRemaining = getDaysUntilDate(customer.commitmentEndsAt);

  return {
    registrationMode: customer.registrationMode ?? null,
    billingStatus: customer.billingStatus ?? null,
    accessState: customer.accessState ?? null,
    accessBlockedAt: customer.accessBlockedAt ?? null,
    pastDueAt: customer.pastDueAt ?? null,
    billingPreference: customer.billingPreference ?? null,
    chargedPriceCents:
      typeof customer.chargedPriceCents === 'number' ? customer.chargedPriceCents : null,
    commitmentEndsAt: customer.commitmentEndsAt ?? null,
    commitmentDaysRemaining,
    trialDaysRemaining:
      typeof customer.trialDaysRemaining === 'number' ? customer.trialDaysRemaining : null,
    planSlug: getSubscriptionPlanSlug(customer.subscriptionPlan) || null,
    planName: plan?.name ?? null,
    isAiActivated: plan?.isAiActivated === true || plan?.features?.ai === true,
  };
};

export const shouldShowGraceCommitmentBanner = (snapshot: CustomerSubscriptionSnapshot): boolean =>
  snapshot.accessState === 'grace_commitment';

export const shouldShowGracePastDueBanner = (snapshot: CustomerSubscriptionSnapshot): boolean =>
  snapshot.accessState === 'grace_past_due';

export const shouldShowBlockedAccessBanner = (snapshot: CustomerSubscriptionSnapshot): boolean =>
  snapshot.accessState === 'blocked';

export const shouldShowCancelledRenewalBanner = (snapshot: CustomerSubscriptionSnapshot): boolean =>
  snapshot.billingStatus === 'cancelled' &&
  snapshot.accessState !== 'grace_commitment' &&
  snapshot.accessState !== 'active';

export const shouldShowPastDueRenewalBanner = (snapshot: CustomerSubscriptionSnapshot): boolean =>
  snapshot.billingStatus === 'past_due' && snapshot.accessState !== 'grace_past_due';

export const shouldShowPendingBillingBanner = (snapshot: CustomerSubscriptionSnapshot): boolean =>
  snapshot.billingStatus === 'pending';

export const shouldShowCommitmentEndingBanner = (
  snapshot: CustomerSubscriptionSnapshot
): boolean => {
  if (snapshot.accessState !== 'active' && snapshot.billingStatus !== 'active') return false;
  if (!snapshot.planSlug || snapshot.commitmentDaysRemaining == null) return false;
  return (
    snapshot.commitmentDaysRemaining >= 0 &&
    snapshot.commitmentDaysRemaining <= COMMITMENT_RENEWAL_WARNING_DAYS
  );
};

export const getCommitmentEndingSeverity = (
  commitmentDaysRemaining: number | null
): CommitmentEndingSeverity | null => {
  if (commitmentDaysRemaining == null || commitmentDaysRemaining < 0) return null;
  if (commitmentDaysRemaining <= COMMITMENT_RENEWAL_CRITICAL_DAYS) return 'error';
  if (commitmentDaysRemaining <= COMMITMENT_RENEWAL_URGENT_DAYS) return 'warning';
  if (commitmentDaysRemaining <= COMMITMENT_RENEWAL_WARNING_DAYS) return 'info';
  return null;
};

let cachedSnapshot: CustomerSubscriptionSnapshot = { ...EMPTY_SNAPSHOT };

export const withActiveBillingSnapshot = (
  snapshot: CustomerSubscriptionSnapshot
): CustomerSubscriptionSnapshot => ({
  ...snapshot,
  billingStatus: 'active',
  accessState: 'active',
});

export const patchCustomersBillingActive = (
  customers: ICustomer[] | undefined
): ICustomer[] | undefined => {
  if (!customers?.length) return customers;

  return customers.map((customer, index) =>
    index === 0 && customer.billingStatus !== 'active'
      ? {
          ...customer,
          billingStatus: 'active' as CustomerBillingStatus,
          accessState: 'active' as CustomerBillingAccessState,
        }
      : customer
  );
};

export const setCustomerSubscriptionCache = (snapshot: CustomerSubscriptionSnapshot): void => {
  cachedSnapshot = snapshot;
};

export const getCustomerSubscriptionCache = (): CustomerSubscriptionSnapshot => cachedSnapshot;

export const clearCustomerSubscriptionCache = (): void => {
  cachedSnapshot = { ...EMPTY_SNAPSHOT };
};
